import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  PackagePlus,
  RotateCcw,
  Search,
  Undo2,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/simple-toaster';
import { AddLocationDialog } from '@/components/shared/add-location-dialog';
import { useAllCFLocations } from '@/hooks/use-cf-locations';
import {
  useReceiveGear,
  useUndoReceivedAsset,
  useUpdateReceivedAssetManufactureDate,
} from '@/hooks/use-assets';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useGearModelSearch } from '@/hooks/use-gear-models';
import type {
  GearModel,
  ReceiveGearEntryMethod,
  ReceiveGearItem,
  ReceiveGearPayload,
  ReceiveGearResult,
} from '@/lib/types';
import {
  buildReceiveGearCsv,
  parseOptionalDayInterval,
  parseSerialInput,
  replaceRetriedResult,
} from './receive-gear-utils';
import type { ReceiveGearIntakeResult } from './receive-gear-utils';
import {
  BATCH_CHUNK_SIZE,
  downloadCsv,
  runInChunks,
  slugifyFilename,
  todayInputValue,
} from './batch-entry/batch-utils';
import type { BatchProgress } from './batch-entry/batch-utils';
import { SerialEntryPanel } from './batch-entry/serial-entry-panel';
import { useScanFeedback } from './batch-entry/use-scan-feedback';

const RECEIVE_CHUNK_SIZE = BATCH_CHUNK_SIZE;

function defaultBatchName(): string {
  return `Receive gear – ${todayInputValue()}`;
}

function errorMessage(error: unknown): string {
  if (isAxiosError<{ error?: string; batch_id?: string[] }>(error)) {
    return error.response?.data.error
      ?? error.response?.data.batch_id?.[0]
      ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return 'The request failed. These serials were not confirmed as saved.';
}

export function BatchAddAssetsPage() {
  const navigate = useNavigate();
  const receiveGear = useReceiveGear();
  const undoReceivedAsset = useUndoReceivedAsset();
  const updateReceivedAssetManufactureDate = useUpdateReceivedAssetManufactureDate();
  const { data: locations = [], isLoading: locationsLoading } = useAllCFLocations();
  const singleInputRef = useRef<HTMLInputElement>(null);
  const resultIdRef = useRef(0);
  const operationQueue = useRef(Promise.resolve());
  const retryingIdsRef = useRef(new Set<number>());

  const [batchId, setBatchId] = useState(() => crypto.randomUUID());
  const [batchName, setBatchName] = useState(defaultBatchName);
  const [modelSearchQuery, setModelSearchQuery] = useState('');
  const debouncedModelSearchQuery = useDebouncedValue(modelSearchQuery, 300);
  const {
    data: modelSearchResults,
    isLoading: modelSearchLoading,
    isError: modelSearchFailed,
  } = useGearModelSearch(debouncedModelSearchQuery);
  const [selectedModel, setSelectedModel] = useState<GearModel | null>(null);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState(todayInputValue);
  const [serviceEntryDate, setServiceEntryDate] = useState(todayInputValue);
  const [batchManufactureDate, setBatchManufactureDate] = useState('');
  const [inspectionInterval, setInspectionInterval] = useState('');
  const [maintenanceInterval, setMaintenanceInterval] = useState('');
  const [supplier, setSupplier] = useState('');
  const [orderReference, setOrderReference] = useState('');
  const [notes, setNotes] = useState('');
  const [recordInitialInspection, setRecordInitialInspection] = useState(true);
  const { feedbackClasses, playFeedback, soundEnabled, toggleSound } = useScanFeedback();

  const [results, setResults] = useState<ReceiveGearIntakeResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<BatchProgress>({ processed: 0, total: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [undoingIds, setUndoingIds] = useState<Set<number>>(new Set());
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());
  const [savingDateIds, setSavingDateIds] = useState<Set<number>>(new Set());

  const sharedDetailsReady = Boolean(
    batchName.trim()
      && selectedModel
      && locationId
      && purchaseDate
      && serviceEntryDate
  );

  useEffect(() => {
    if (sharedDetailsReady && !isFinished) {
      singleInputRef.current?.focus();
    }
  }, [sharedDetailsReady, isFinished]);

  const buildPayload = useCallback((
    items: ReceiveGearItem[],
    entryMethod: ReceiveGearEntryMethod
  ): ReceiveGearPayload | null => {
    if (!sharedDetailsReady || !selectedModel || !locationId) {
      toast.error('Complete the shared batch details before entering serials.');
      return null;
    }

    const inspectionIntervalDays = parseOptionalDayInterval(inspectionInterval);
    const maintenanceIntervalDays = parseOptionalDayInterval(maintenanceInterval);
    if (inspectionIntervalDays === null || maintenanceIntervalDays === null) {
      toast.error('Intervals must be whole numbers of days greater than zero.');
      return null;
    }

    return {
      batch_id: batchId,
      batch_name: batchName.trim(),
      entry_method: entryMethod,
      items,
      type: selectedModel.asset_category,
      manufacturer: selectedModel.manufacturer_name,
      model: selectedModel.name,
      gear_model: selectedModel.id,
      current_location: locationId,
      purchase_date: purchaseDate,
      service_entry_date: serviceEntryDate,
      date_of_manufacture: batchManufactureDate || undefined,
      inspection_interval_days: inspectionIntervalDays,
      maintenance_interval_days: maintenanceIntervalDays,
      supplier: supplier.trim(),
      order_reference: orderReference.trim(),
      notes: notes.trim(),
      record_initial_inspection: recordInitialInspection,
    };
  }, [
    batchId,
    batchName,
    batchManufactureDate,
    inspectionInterval,
    locationId,
    maintenanceInterval,
    notes,
    orderReference,
    purchaseDate,
    recordInitialInspection,
    selectedModel,
    serviceEntryDate,
    sharedDetailsReady,
    supplier,
  ]);

  const appendResults = useCallback((
    nextResults: ReceiveGearResult[],
    entryMethod: ReceiveGearEntryMethod,
    retryResultId?: number
  ) => {
    if (retryResultId !== undefined && nextResults.length === 1) {
      setResults((current) => replaceRetriedResult(
        current,
        retryResultId,
        nextResults[0]
      ));
      setIsFinished(false);
      return;
    }
    const mapped = nextResults.map((result): ReceiveGearIntakeResult => ({
      ...result,
      date_of_manufacture:
        result.date_of_manufacture
        ?? result.proposed_date_of_manufacture
        ?? null,
      id: ++resultIdRef.current,
      entryMethod,
      savedDateOfManufacture: result.date_of_manufacture ?? null,
    }));
    setResults((current) => [...current, ...mapped]);
    setIsFinished(false);
  }, []);

  const submitSerials = useCallback(async (
    items: ReceiveGearItem[],
    entryMethod: ReceiveGearEntryMethod,
    retryResultId?: number
  ) => {
    if (items.length === 0) return;
    const initialPayload = buildPayload(items.slice(0, 1), entryMethod);
    if (!initialPayload) return;

    setIsProcessing(true);
    let created = 0;
    let duplicates = 0;
    let failed = 0;

    await runInChunks(items, RECEIVE_CHUNK_SIZE, async (chunk) => {
      const payload = buildPayload(chunk, entryMethod);
      if (!payload) return;

      try {
        const response = await receiveGear.mutateAsync(payload);
        const returnedCounts = new Map<string, number>();
        for (const result of response.results) {
          const key = result.serial.toLocaleLowerCase();
          returnedCounts.set(key, (returnedCounts.get(key) ?? 0) + 1);
        }
        const missingResults: ReceiveGearResult[] = [];
        for (const item of chunk) {
          const serial = item.serial;
          const key = serial.toLocaleLowerCase();
          const remaining = returnedCounts.get(key) ?? 0;
          if (remaining > 0) {
            returnedCounts.set(key, remaining - 1);
          } else {
            missingResults.push({
              serial,
              date_of_manufacture: item.date_of_manufacture,
              date_format: item.date_of_manufacture ? 'entered' : null,
              status: 'failed',
              message: 'The server did not return a save result for this serial.',
            });
          }
        }
        const chunkResults = [...response.results, ...missingResults];
        appendResults(chunkResults, entryMethod, retryResultId);
        created += chunkResults.filter((result) => result.status === 'created').length;
        duplicates += chunkResults.filter((result) => result.status === 'duplicate').length;
        failed += chunkResults.filter((result) => result.status === 'failed').length;
      } catch (error) {
        const message = errorMessage(error);
        const chunkFailures: ReceiveGearResult[] = chunk.map((item) => ({
          serial: item.serial,
          date_of_manufacture: item.date_of_manufacture,
          date_format: item.date_of_manufacture ? 'entered' : null,
          status: 'failed',
          message,
        }));
        appendResults(chunkFailures, entryMethod, retryResultId);
        failed += chunkFailures.length;
      }
    }, { onProgress: setProgress });

    if (failed > 0) {
      playFeedback('error');
      toast.error(`${failed} serial${failed === 1 ? '' : 's'} failed and can be retried.`);
    } else if (duplicates > 0) {
      playFeedback('duplicate');
      toast.warning(`${created} received; ${duplicates} already in the register.`);
    } else {
      playFeedback('success');
      toast.success(`${created} item${created === 1 ? '' : 's'} received.`);
    }

    setIsProcessing(false);
    singleInputRef.current?.focus();
  }, [appendResults, buildPayload, playFeedback, receiveGear]);

  const enqueueSingleSerial = useCallback((
    serial: string,
    retryResultId?: number
  ) => {
    const trimmed = serial.trim();
    if (!trimmed) return;
    if (!sharedDetailsReady) {
      toast.error('Complete the shared batch details before entering serials.');
      return;
    }
    if (retryResultId !== undefined) {
      if (retryingIdsRef.current.has(retryResultId)) return;
      retryingIdsRef.current.add(retryResultId);
      setRetryingIds(new Set(retryingIdsRef.current));
    }

    setIsFinished(false);
    singleInputRef.current?.focus();
    operationQueue.current = operationQueue.current.then(
      async () => {
        try {
          const priorResult = retryResultId === undefined
            ? undefined
            : results.find((result) => result.id === retryResultId);
          const retryDate = priorResult?.date_format === 'entered'
            ? priorResult.date_of_manufacture ?? undefined
            : undefined;
          if (retryDate) {
            const parsed = parseSerialInput(`${trimmed}, ${retryDate}`);
            if (parsed.errors.length > 0) {
              toast.error(parsed.errors[0].message);
              return;
            }
          }
          await submitSerials(
            [{
              serial: trimmed,
              date_of_manufacture: retryDate,
            }],
            'single',
            retryResultId
          );
        } finally {
          if (retryResultId !== undefined) {
            retryingIdsRef.current.delete(retryResultId);
            setRetryingIds(new Set(retryingIdsRef.current));
          }
        }
      }
    );
  }, [results, sharedDetailsReady, submitSerials]);

  const handleUndo = async (result: ReceiveGearIntakeResult) => {
    if (!result.asset_id || result.undone) return;
    setUndoingIds((current) => new Set(current).add(result.id));
    try {
      await undoReceivedAsset.mutateAsync({
        id: result.asset_id,
        batchId,
      });
      setResults((current) => current.map((item) => (
        item.id === result.id ? { ...item, undone: true } : item
      )));
      playFeedback('success');
      toast.success(`${result.serial} removed from this intake.`);
    } catch (error) {
      playFeedback('error');
      toast.error(errorMessage(error));
    } finally {
      setUndoingIds((current) => {
        const next = new Set(current);
        next.delete(result.id);
        return next;
      });
    }
  };

  const activeResults = results.filter((result) => !result.undone);
  const createdResults = activeResults.filter((result) => result.status === 'created');
  const duplicateResults = activeResults.filter((result) => result.status === 'duplicate');
  const failedResults = activeResults.filter((result) => result.status === 'failed');
  const recentResults = [...activeResults].reverse();
  const sharedDetailsLocked = isProcessing
    || results.some((result) => result.status === 'created');

  const updateResultDate = (resultId: number, value: string) => {
    setResults((current) => current.map((result) => (
      result.id === resultId
        ? {
            ...result,
            date_of_manufacture: value || null,
            date_format: 'entered',
          }
        : result
    )));
  };

  const saveResultDate = async (result: ReceiveGearIntakeResult) => {
    if (!result.asset_id) return;
    setSavingDateIds((current) => new Set(current).add(result.id));
    try {
      await updateReceivedAssetManufactureDate.mutateAsync({
        id: result.asset_id,
        batchId,
        dateOfManufacture: result.date_of_manufacture || null,
      });
      setResults((current) => current.map((item) => (
        item.id === result.id
          ? {
              ...item,
              savedDateOfManufacture: item.date_of_manufacture ?? null,
            }
          : item
      )));
      toast.success(`Manufacture date updated for ${result.serial}.`);
    } catch (error) {
      setResults((current) => current.map((item) => (
        item.id === result.id
          ? {
              ...item,
              date_of_manufacture: item.savedDateOfManufacture,
            }
          : item
      )));
      toast.error(errorMessage(error));
    } finally {
      setSavingDateIds((current) => {
        const next = new Set(current);
        next.delete(result.id);
        return next;
      });
    }
  };

  const retryFailed = () => {
    for (const result of failedResults) {
      enqueueSingleSerial(result.serial, result.id);
    }
  };

  const downloadSummary = () => {
    downloadCsv(
      `${slugifyFilename(batchName, 'receive-gear')}-summary.csv`,
      buildReceiveGearCsv(batchId, batchName, results)
    );
  };

  const startNewBatch = () => {
    setBatchId(crypto.randomUUID());
    setBatchName(defaultBatchName());
    setSupplier('');
    setOrderReference('');
    setNotes('');
    setBatchManufactureDate('');
    setResults([]);
    setProgress({ processed: 0, total: 0 });
    setIsFinished(false);
    window.setTimeout(() => singleInputRef.current?.focus());
  };

  return (
    <div id="batch-add-assets-fields" className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/assets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Receive gear</h1>
            <p className="text-gray-600 mt-1">Set shared details once, then scan, paste, or generate serials.</p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={toggleSound}
          aria-label={soundEnabled ? 'Mute intake sounds' : 'Enable intake sounds'}
        >
          {soundEnabled ? <Volume2 className="h-4 w-4 mr-2" /> : <VolumeX className="h-4 w-4 mr-2" />}
          {soundEnabled ? 'Sound on' : 'Sound off'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Shared details</CardTitle>
          <CardDescription>
            These details are applied to every asset in this batch.
            {sharedDetailsLocked ? ' Start a new batch to change them.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <fieldset disabled={sharedDetailsLocked} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="batch-name">Batch name *</Label>
              <Input id="batch-name" value={batchName} onChange={(event) => setBatchName(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={locationId ? String(locationId) : undefined}
                onValueChange={(value) => {
                  if (value === '__add_location__') {
                    setLocationDialogOpen(true);
                    return;
                  }
                  setLocationId(Number(value));
                }}
              >
                <SelectTrigger
                  id="location"
                  data-required="true"
                  data-required-empty={locationId ? 'false' : 'true'}
                  data-required-message="Location is required."
                >
                  <SelectValue placeholder={locationsLoading ? 'Loading locations…' : 'Select a location'} />
                </SelectTrigger>
                <SelectContent>
                  {locations.filter((location) => location.is_active).map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__add_location__">+ Add location</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div
            className="space-y-3"
            data-required="true"
            data-required-empty={selectedModel ? 'false' : 'true'}
            data-required-message="Manufacturer and model are required."
            tabIndex={-1}
          >
            <Label htmlFor="model-search">Manufacturer and model *</Label>
            {selectedModel ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-medium text-green-900">
                    {selectedModel.manufacturer_name} / {selectedModel.name}
                  </p>
                  <p className="text-sm text-green-700">
                    {selectedModel.asset_category_display}
                    {selectedModel.model_number ? ` · ${selectedModel.model_number}` : ''}
                    {selectedModel.service_life_years ? ` · ${selectedModel.service_life_years}-year service life` : ''}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedModel(null);
                    setModelSearchQuery('');
                  }}
                >
                  Change model
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="model-search"
                    className="pl-10"
                    placeholder="Search model, manufacturer, or SKU"
                    value={modelSearchQuery}
                    onChange={(event) => setModelSearchQuery(event.target.value)}
                  />
                </div>
                {modelSearchLoading && <p className="text-sm text-gray-500">Searching…</p>}
                {modelSearchFailed && <p className="text-sm text-red-600">The gear-model catalog could not be loaded.</p>}
                {modelSearchResults && modelSearchResults.length > 0 && (
                  <div className="max-h-64 overflow-y-auto rounded-lg border divide-y">
                    {modelSearchResults.map((model) => (
                      <button
                        key={model.id}
                        type="button"
                        className="w-full p-3 text-left hover:bg-gray-50 flex items-start justify-between gap-3"
                        onClick={() => {
                          setSelectedModel(model);
                          setModelSearchQuery('');
                          setInspectionInterval(
                            model.default_inspection_interval_days
                              ? String(model.default_inspection_interval_days)
                              : ''
                          );
                        }}
                      >
                        <span>
                          <span className="block font-medium">{model.name}</span>
                          <span className="block text-sm text-gray-600">{model.manufacturer_name}</span>
                        </span>
                        <Badge variant="secondary">{model.asset_category_display}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <Label htmlFor="purchase-date">Purchase date *</Label>
              <Input id="purchase-date" type="date" value={purchaseDate} onChange={(event) => setPurchaseDate(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-date">In-service date *</Label>
              <Input id="service-date" type="date" value={serviceEntryDate} onChange={(event) => setServiceEntryDate(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manufacture-date">Manufacture date (optional)</Label>
              <Input
                id="manufacture-date"
                type="date"
                value={batchManufactureDate}
                onChange={(event) => setBatchManufactureDate(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inspection-interval">Inspection interval (days)</Label>
              <Input id="inspection-interval" type="number" min="1" step="1" value={inspectionInterval} onChange={(event) => setInspectionInterval(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maintenance-interval">Maintenance interval (days)</Label>
              <Input id="maintenance-interval" type="number" min="1" step="1" value={maintenanceInterval} onChange={(event) => setMaintenanceInterval(event.target.value)} />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier (optional)</Label>
              <Input id="supplier" value={supplier} onChange={(event) => setSupplier(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-reference">Order / PO reference (optional)</Label>
              <Input id="order-reference" value={orderReference} onChange={(event) => setOrderReference(event.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="batch-notes">Notes (optional)</Label>
            <Textarea id="batch-notes" value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
          </div>
          <div className="flex items-start gap-3 rounded-lg border p-4">
            <Checkbox
              id="initial-inspection"
              checked={recordInitialInspection}
              onCheckedChange={(checked) => setRecordInitialInspection(checked === true)}
            />
            <div>
              <Label htmlFor="initial-inspection">Record initial inspection (pass) for each item</Label>
              <p className="text-sm text-gray-500 mt-1">
                Signed silently by the logged-in competent person with the date and serial.
              </p>
            </div>
          </div>
          </fieldset>
        </CardContent>
      </Card>

      {!isFinished && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle>2. Enter serials</CardTitle>
              <CardDescription>All three methods use the same duplicate, failure, inspection, and audit rules.</CardDescription>
            </CardHeader>
            <CardContent>
              <SerialEntryPanel
                key={batchId}
                singleInputRef={singleInputRef}
                ready={sharedDetailsReady}
                disabledPlaceholder="Complete shared details first"
                processing={isProcessing}
                progress={progress}
                progressLabel="Saving"
                actionVerb="Receive"
                pastePlaceholder={'SN001\nSN002, 2022-08-15\nSN003'}
                rangeSummarySuffix="will be created"
                validationScope="batch-add-assets-fields"
                onSingle={(serial) => enqueueSingleSerial(serial)}
                onBatch={(items, method) => submitSerials(items, method)}
              />
            </CardContent>
          </Card>

          <Card className={`transition-colors ${feedbackClasses}`} aria-live="polite">
            <CardHeader>
              <CardTitle>{createdResults.length.toLocaleString()} received</CardTitle>
              <CardDescription>
                {duplicateResults.length} duplicates · {failedResults.length} failed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {failedResults.length > 0 && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-300 bg-red-50 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-red-900">
                      {failedResults.length} serial{failedResults.length === 1 ? '' : 's'} failed
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isProcessing}
                      onClick={retryFailed}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Retry failed
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y divide-red-200">
                    {failedResults.map((result) => (
                      <div key={`failed-${result.id}`} className="py-2 space-y-2">
                        <p className="font-mono text-sm text-red-950">{result.serial}</p>
                        <p className="text-sm text-red-800">{result.message}</p>
                        {result.date_format === 'entered' && (
                          <div className="space-y-1">
                            <Label
                              htmlFor={`failed-manufacture-date-${result.id}`}
                              className="text-xs text-red-950"
                            >
                              Correct manufacture date
                            </Label>
                            <Input
                              id={`failed-manufacture-date-${result.id}`}
                              type="text"
                              inputMode="numeric"
                              placeholder="YYYY-MM-DD"
                              className="h-8 bg-white"
                              value={result.date_of_manufacture ?? ''}
                              disabled={
                                isProcessing || retryingIds.has(result.id)
                              }
                              onChange={(event) => updateResultDate(
                                result.id,
                                event.target.value
                              )}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {recentResults.length === 0 ? (
                <p className="text-sm text-gray-500">Recent serials will appear here.</p>
              ) : (
                <div className="max-h-[36rem] overflow-y-auto divide-y rounded-lg border bg-white">
                  {recentResults.map((result) => (
                    <div key={result.id} className="p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-sm truncate">{result.serial}</p>
                        <p className={`text-xs ${
                          result.status === 'created'
                            ? 'text-green-700'
                            : result.status === 'duplicate'
                              ? 'text-amber-700'
                              : 'text-red-700'
                        }`}>
                          {result.message}
                        </p>
                        {result.status === 'created' ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Label
                              htmlFor={`manufacture-date-${result.id}`}
                              className="text-xs"
                            >
                              {result.savedDateOfManufacture
                                && result.date_format !== 'entered'
                                && result.date_format !== 'batch_default'
                                ? 'Detected manufacture date'
                                : result.date_of_manufacture
                                  && !result.savedDateOfManufacture
                                  ? `Suggested: ${result.date_of_manufacture}, confirm?`
                                  : 'Manufacture date'}
                            </Label>
                            <Input
                              id={`manufacture-date-${result.id}`}
                              type="date"
                              className="h-8 w-auto"
                              value={result.date_of_manufacture ?? ''}
                              disabled={
                                isProcessing || savingDateIds.has(result.id)
                              }
                              onChange={(event) => updateResultDate(
                                result.id,
                                event.target.value
                              )}
                            />
                            {!result.date_of_manufacture && (
                              <span className="text-xs text-gray-500">
                                Not detected
                              </span>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={
                                isProcessing || savingDateIds.has(result.id)
                              }
                              onClick={() => void saveResultDate(result)}
                            >
                              Save date
                            </Button>
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-gray-500">
                            {result.status === 'failed'
                              ? 'Proposed manufacture date'
                              : 'Manufacture date'}: {result.date_of_manufacture || 'not detected'}
                          </p>
                        )}
                      </div>
                      {result.status === 'created' && result.asset_id && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={undoingIds.has(result.id)}
                          onClick={() => void handleUndo(result)}
                        >
                          <Undo2 className="h-4 w-4 mr-1" />
                          Undo
                        </Button>
                      )}
                      {result.status === 'failed' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={retryingIds.has(result.id)}
                          onClick={() => enqueueSingleSerial(result.serial, result.id)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Retry
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {activeResults.length > 0 && (
                <Button type="button" className="w-full" onClick={() => setIsFinished(true)} disabled={isProcessing}>
                  Finish batch
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {isFinished && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>3. Batch summary</CardTitle>
                <CardDescription>{batchName}</CardDescription>
              </div>
              <Button type="button" variant="outline" onClick={downloadSummary}>
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <CheckCircle2 className="h-5 w-5 text-green-700 mb-2" />
                <p className="text-2xl font-bold text-green-900">{createdResults.length}</p>
                <p className="text-sm text-green-700">Created</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <PackagePlus className="h-5 w-5 text-amber-700 mb-2" />
                <p className="text-2xl font-bold text-amber-900">{duplicateResults.length}</p>
                <p className="text-sm text-amber-700">Already in register</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <AlertCircle className="h-5 w-5 text-red-700 mb-2" />
                <p className="text-2xl font-bold text-red-900">{failedResults.length}</p>
                <p className="text-sm text-red-700">Failed</p>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {[
                { title: 'Created serials', items: createdResults, empty: 'No assets created.' },
                { title: 'Duplicates skipped', items: duplicateResults, empty: 'No duplicates.' },
                { title: 'Failed serials', items: failedResults, empty: 'No failures.' },
              ].map((section) => (
                <div key={section.title} className="space-y-2">
                  <h3 className="font-medium">{section.title}</h3>
                  <div className="max-h-72 overflow-y-auto rounded-lg border divide-y">
                    {section.items.length === 0 ? (
                      <p className="p-3 text-sm text-gray-500">{section.empty}</p>
                    ) : section.items.map((result) => (
                      <div key={result.id} className="p-3">
                        <p className="font-mono text-sm">{result.serial}</p>
                        <p className="text-xs text-gray-500">
                          {result.status === 'failed'
                            ? 'Proposed manufacture date'
                            : result.savedDateOfManufacture === null
                              ? 'Suggested manufacture date'
                              : 'Manufacture date'}: {result.date_of_manufacture || 'not detected'}
                        </p>
                        <p className="text-xs text-gray-500">{result.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button type="button" variant="outline" asChild>
                <Link to="/assets">Back to assets</Link>
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFinished(false)}>
                  Continue receiving
                </Button>
                <Button type="button" onClick={startNewBatch}>Start new batch</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <AddLocationDialog
        open={locationDialogOpen}
        onOpenChange={setLocationDialogOpen}
        onCreated={(location) => setLocationId(location.id)}
      />
    </div>
  );
}
